import { FC, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Loading from "web/Layout/Common/Loading";
import __ from "web/Layout/Translations";

import Button from "web/Components/Common/ButtonCC";

import CheckIcon from "web/assets/icons/check.svg";

import useCodeItemStatus from "web/hooks/useCodeItemStatus";
import useInternetConnection from "web/hooks/useInternetConnection/useInternetConnection";
import { ModalActionTypes } from "web/hooks/useModal/useModalTypes";

import formatDate from "web/utils/data/parser/dateAndTime/formatDate";
import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import { ProductCodeFrontendStatus } from "web/types/Code";

import { useAppContext } from "web/context/app";
import {
  IProductCodeLine,
  useGetBarCodeQuery,
  useGetCodeSettingsQuery,
  useGetProductCodeQuery,
} from "web/features/codes/codesApiSlice";
import { setCodeUsed } from "web/features/codes/codesThunks";

import CodeLine from "../CodeLine";
import Loader from "../Loader";
import DownloadCodeButton from "./DownloadCodeButton";
import classes from "./codeModal.scss";
import printCode from "./utils/printCode";
import printCodeDirectly from "./utils/printCodeDirectly";

interface ICodeModalContainerProps {
  productName: string;
  itemId: number;
  orderPaid: boolean;
  sku: string;
  product_expiration_date?: string;
  userUsedDate?: string;
  userUsed?: boolean | number;
  productCodeFrontendStatus?: ProductCodeFrontendStatus;
  onAvailabilityChange?: (isAvailable: boolean) => void;
}

const CodeModalContainer: FC<ICodeModalContainerProps> = ({
  productName,
  itemId,
  orderPaid,
  sku,
  product_expiration_date = "",
  userUsedDate = "",
  userUsed = false,
  productCodeFrontendStatus = "" as ProductCodeFrontendStatus,
  onAvailabilityChange,
}) => {
  const dispatch = useDispatch();
  const { settingMarkIds = [] } = useSelector((state) => state.codes);
  const { modal } = useAppContext();
  const [isPrinting, setIsPrinting] = useState(false);
  const isSettingMark = settingMarkIds.some((id) => id === itemId);

  const isUsed = !!userUsed;

  const { switchable } = useCodeItemStatus(productCodeFrontendStatus);

  const { data: codeSettingsData, isLoading: codeSettingsLoading } =
    useGetCodeSettingsQuery(sku);
  const canPrintCode = !!codeSettingsData?.canPrintCode;

  const { data: codeData, isLoading: codeLoading } = useGetProductCodeQuery(
    itemId,
    {
      skip: !orderPaid,
    }
  );
  const { code, isSplitted, isNewStructure } = codeData || {};

  const { data: barcode, isLoading: barcodeLoading } = useGetBarCodeQuery(
    itemId,
    {
      skip: !orderPaid || !canPrintCode,
    }
  );

  const { isDisconnectedWebView } = useInternetConnection();

  const print = useCallback(
    () => printCode({ itemId, setIsPrinting }),
    [itemId]
  );

  // eslint-disable-next-line no-underscore-dangle
  const _code =
    (isSplitted
      ? (code?.[0] as IProductCodeLine)?.value ?? code?.[0]
      : (code as string)) ?? null;

  const onPrintCodeDirectlyHandler = () =>
    printCodeDirectly({ productName, code: _code });

  const closeModal = useCallback(() => {
    modal.dispatch({ type: ModalActionTypes.RESET });
  }, [modal]);

  const onToggleUsedHandler = useCallback(async () => {
    await dispatch(setCodeUsed({ itemId, isUsed: isUsed ? 0 : 1 }));
    onAvailabilityChange?.(isUsed);
    closeModal();
  }, [dispatch, isUsed, itemId, onAvailabilityChange]);

  const expirationDateMilliseconds = product_expiration_date
    ? Date.parse(product_expiration_date)
    : null;
  const formattedExpirationDate = expirationDateMilliseconds
    ? formatDate(expirationDateMilliseconds)
    : null;

  const formattedUsedDate = userUsedDate ? formatDate(userUsedDate) : null;

  const codeLineClasses = {
    codeLine: classes.codeLine,
    codeLineName: classes.codeLineName,
    codeLineValue: classes.codeLineValue,
    codeLineCopyButton: classes.codeLineCopyButton,
    codeLineCopyIcon: classes.codeLineCopyIcon,
  };

  const loading = codeSettingsLoading || codeLoading || barcodeLoading;

  return (
    <div className={`${classes.root} ${isUsed ? classes.rootUsed : ""}`}>
      <div className={classes.title} data-t1="code_modal_title">
        <h2 className={classes.productName} data-t1="code_modal_product_name">
          {productName}
        </h2>
      </div>
      <div className={classes.box}>
        {formattedUsedDate && isUsed ? (
          <p className={classes.usedDate}>
            {__("Kod wykorzystany")}{" "}
            <span className={classes.usedDateValue}>{formattedUsedDate}</span>
            <CheckIcon className={classes.checkIcon} aria-hidden />
          </p>
        ) : null}
        {loading ? (
          <div className={classes.loadingContainer}>
            <Loading />
          </div>
        ) : (
          <div className={classes.codeContainer}>
            {formattedExpirationDate ? (
              <p
                className={`${classes.expirationDate} ${
                  userUsed === 1 ? classes.expirationDateUsed : ""
                }`}
              >
                {__("Kod ważny do")}{" "}
                <span className={classes.expirationDateValue}>
                  {formattedExpirationDate}
                </span>
              </p>
            ) : null}
            {barcode && canPrintCode ? (
              <div className={classes.codeImageContainer}>
                <img className={classes.codeImage} src={barcode} alt="" />
              </div>
            ) : null}
            {code && (
              <div className={classes.codeLines}>
                {isSplitted && isArrayHasItems(code) ? (
                  code.map((line) =>
                    isNewStructure ? (
                      <CodeLine
                        key={(line as IProductCodeLine)?.name}
                        label={(line as IProductCodeLine)?.name}
                        value={(line as IProductCodeLine)?.value}
                        dataT1="code_modal_code"
                        classes={codeLineClasses}
                      />
                    ) : (
                      <CodeLine
                        key={line as string}
                        className={classes.codeLine}
                        data-t1="code_modal_code"
                        value={line as string}
                        classes={codeLineClasses}
                      />
                    )
                  )
                ) : (
                  <CodeLine
                    key="code"
                    value={code as string}
                    dataT1="code_modal_code"
                    classes={codeLineClasses}
                  />
                )}
              </div>
            )}
            <div className={classes.buttons}>
              <DownloadCodeButton
                hasCode={!!code}
                canPrintCode={canPrintCode}
                isPrinting={isPrinting}
                onPrintHandler={print}
                onPrintCodeDirectlyHandler={onPrintCodeDirectlyHandler}
              />
            </div>
            {isDisconnectedWebView && (!barcode || !code) && (
              <p>{__("Brak połączenia z siecią")}</p>
            )}
          </div>
        )}
      </div>
      {switchable ? (
        <div className={classes.footer}>
          <Button
            variant="secondary"
            onClick={isUsed ? onToggleUsedHandler : closeModal}
            disabled={isSettingMark}
            className={classes.button}
          >
            {isUsed
              ? __("Oznacz jako niewykorzystany")
              : __("Zamknij i wykorzystaj później")}
          </Button>
          <Button
            variant="primary"
            onClick={isUsed ? closeModal : onToggleUsedHandler}
            className={classes.button}
          >
            {isUsed ? __("Zamknij") : __("Oznacz jako wykorzystany")}
          </Button>
        </div>
      ) : (
        <div className={classes.footer}>
          <Button
            variant="primary"
            onClick={closeModal}
            className={`${classes.button} ${classes.closeButton}`}
          >
            {__("Zamknij")}
          </Button>
        </div>
      )}
      <Loader isLoading={isSettingMark} />
    </div>
  );
};

export default CodeModalContainer;
